// Forked from api/msw/api/caseEmails/mockData.ts (Service Requests start as a copy of Cases).
// Comments below were written for Cases and still describe this file until the two diverge.
import {
  _storeEntityDataListToLocalForage,
  getEntityListDataFromLocalForage,
} from '../../modules/mockDataStorageManager'
import {
  READ_STATUS,
  inReplyToRefusal,
  readStatusChangeRefusal,
  readStatusItem,
} from '../../modules/emailReadStatusRules'
import type { IServiceRequestEmail } from '~/types/serviceRequest/models'
import type { TEntityId } from '~/types/models'

const mn = '[api/msw/api/serviceRequestEmails/mockData.ts]'

/**
 * Service request emails: the envelope, the transport, and the readable
 * `body` -- the shape case emails took in cynque-api-server #1737, when the text
 * moved off a linked note (`serviceRequestNoteId`, now gone) onto the email.
 *
 * ⚠ Hand-written rather than built with makeNoteMockData / makeDocumentMockData,
 * because IServiceRequestEmail is deliberately NOT an entity -- no version, no hrid, no
 * canBeDeleted. An arrived message is append-only and never edited, so the
 * concurrency and CRUD machinery those factories assume would serve mutations
 * that do not happen.
 */

/**
 * The seed row, with the mock's own bookkeeping.
 *
 * ⚠ `_caseId` is NOT on the wire. `case_emails` has a real case column
 * server-side and the overview view groups BY it, so the case is implicit in the
 * payload and `ServiceRequestEmail` carries no case field. Keeping it underscored and
 * stripping it on the way out means the mock cannot end up WIDER than the server
 * -- the mirror of the narrower-than-the-server trap, and just as good at hiding
 * a defect.
 */
type IServiceRequestEmailSeed = IServiceRequestEmail & { _caseId: TEntityId }

export async function getStartingServiceRequestEmailListData(): Promise<
  IServiceRequestEmailSeed[]
> {
  return [
    // The customer's original message.
    {
      _caseId: 'XXSR0001',
      id: 'XXSRE0001',
      direction: 'inbound',
      body:
        'Since Monday the Boston line drops about one call in five, usually ' +
        'after a minute or two. Happens on all four handsets. Invoice and a ' +
        'photo of the NIU attached.',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'POTS line at Boston dropping calls',
      fromAddress: 'dana.whitfield@northwindlogistics.com',
      fromName: 'Dana Whitfield',
      toAddresses: ['servicerequests@wxtechnologygroup.com'],
      ccAddresses: ['ops@northwindlogistics.com'],
      replyTo: null,
      sentAt: '2022-03-01T14:02:11Z',
      receivedAt: '2022-03-01T14:02:47Z',
      createdAt: '2022-03-01T14:02:47Z',
      messageId: '<CAF1a9c2@northwindlogistics.com>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-8f2c14aa',
      mailboxHash: 'case-XXSR0001',
      spamScore: 0.4,
      // Answered by XXSRE0002.
      readStatus: readStatusItem(READ_STATUS.REPLIED),
    },
    // The agent's reply -- and it BOUNCED. This is the row the UI must not hide:
    // there is no notification mechanism behind deliveryState, so a case that
    // swallows this leaves the agent believing they replied while the customer
    // believes they were ignored.
    {
      _caseId: 'XXSR0001',
      id: 'XXSRE0002',
      direction: 'outbound',
      body:
        'Thanks Dana. We are dispatching a technician for Thursday morning ' +
        'and have opened a trouble ticket with the carrier in the meantime.',
      sentBy: { id: 'XXTM0006', label: 'Service Technician' },
      repeatOf: null,
      deliveryState: 'bounced',
      subject: 'Re: POTS line at Boston dropping calls',
      fromAddress: 'servicerequests@wxtechnologygroup.com',
      fromName: 'WX Technology Group Support',
      toAddresses: ['dana.whitfield@northwindlogistics.com'],
      ccAddresses: null,
      replyTo: 'servicerequests@wxtechnologygroup.com',
      sentAt: '2022-03-01T16:40:00Z',
      receivedAt: null,
      createdAt: '2022-03-01T16:39:52Z',
      messageId: '<9d31be04@wxtechnologygroup.com>',
      inReplyTo: '<CAF1a9c2@northwindlogistics.com>',
      messageReferences: ['<CAF1a9c2@northwindlogistics.com>'],
      providerMessageId: 'pm-77b90c31',
      mailboxHash: 'case-XXSR0001',
      spamScore: null,
      // Outbound: no read status.
      readStatus: null,
    },
    // ⚠ body NULL: an out-of-office auto-reply. Logged, but deliberately not
    // surfaced as readable text -- a null body is exactly what keeps
    // auto-replies, bounce notifications and over-threshold spam off the Emails
    // card. The overview DOES return it, so the card must filter it out.
    {
      _caseId: 'XXSR0001',
      id: 'XXSRE0003',
      direction: 'inbound',
      body: null,
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'Automatic reply: POTS line at Boston dropping calls',
      fromAddress: 'dana.whitfield@northwindlogistics.com',
      fromName: 'Dana Whitfield',
      toAddresses: ['servicerequests@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-01T16:40:30Z',
      receivedAt: '2022-03-01T16:40:58Z',
      createdAt: '2022-03-01T16:40:58Z',
      messageId: '<ooo-5521@northwindlogistics.com>',
      inReplyTo: '<9d31be04@wxtechnologygroup.com>',
      messageReferences: ['<CAF1a9c2@northwindlogistics.com>'],
      providerMessageId: 'pm-1c0d5e77',
      mailboxHash: 'case-XXSR0001',
      spamScore: 1.1,
      // Opened, never answered (an auto-reply).
      readStatus: readStatusItem(READ_STATUS.READ),
    },
    // A second correspondent, on XXSR0003, whose address is SUPPRESSED. Exists so
    // the reply endpoint's 409 can actually occur locally: the server refuses a
    // reply to an address Postmark has suppressed after a bounce, and a mock that
    // cannot produce that leaves the whole recovery path untestable.
    //
    // ⚠ XXSR0002 deliberately keeps NO emails -- it is the control for "a case
    // opened in the app has nobody to reply to", which is a different refusal
    // (400) and must stay distinguishable from this one.
    {
      _caseId: 'XXSR0003',
      id: 'XXSRE0004',
      direction: 'inbound',
      body:
        'Power came back around 6am but the line has been dead since. ' +
        'Nothing on the handset at all.',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'No dial tone after the power cut',
      fromAddress: SUPPRESSED_ADDRESS,
      fromName: 'Ray Kowalski',
      toAddresses: ['servicerequests@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-06T09:14:00Z',
      receivedAt: '2022-03-06T09:14:31Z',
      createdAt: '2022-03-06T09:14:31Z',
      messageId: '<pwr-3312@brightmill.example>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-40ab19c2',
      mailboxHash: 'case-XXSR0003',
      spamScore: 0.2,
      // Not yet opened.
      readStatus: readStatusItem(READ_STATUS.UNREAD),
    },
    // ⚠ THE MESSAGE THAT CREATED XXSR0005. `inReplyTo` and `messageReferences`
    // are BOTH null, which is what makes it a case-opening message rather than
    // a reply into an existing thread -- the pathway had no thread to join.
    // Its sender resolves to a known contact, so the case it opened carries one.
    {
      _caseId: 'XXSR0005',
      id: 'XXSRE0005',
      direction: 'inbound',
      body:
        'Every call out of the Sunset office has heavy static from about ' +
        'ten seconds in. Started yesterday afternoon. Six people affected, ' +
        'all on the same floor.',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'Static on every call from the Sunset office',
      fromAddress: 'bilbo@bostoncoach.com',
      fromName: 'Bilbo Baggins',
      toAddresses: ['servicerequests@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-12T09:14:51Z',
      receivedAt: '2022-03-12T09:15:22Z',
      createdAt: '2022-03-12T09:15:22Z',
      messageId: '<b2f7ce10@bostoncoach.com>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-3ae0b912',
      mailboxHash: 'case-XXSR0005',
      spamScore: 0.2,
      // Not yet opened.
      readStatus: readStatusItem(READ_STATUS.UNREAD),
    },
    // The message that created XXSR0006, from an address no contact owns. The
    // envelope still carries the sender's name; the CASE carries no contact,
    // because a name on an envelope is not a record in the system.
    {
      _caseId: 'XXSR0006',
      id: 'XXSRE0006',
      direction: 'inbound',
      body:
        'We have moved. Could you update the billing address to 44 Cornhill, ' +
        'Suite 3, Boston MA 02108 before the next invoice run?',
      sentBy: null,
      repeatOf: null,
      deliveryState: null,
      subject: 'Please update the billing address on our account',
      fromAddress: 'accounts@quarrystonepartners.com',
      fromName: 'Elaine Brooker',
      toAddresses: ['servicerequests@wxtechnologygroup.com'],
      ccAddresses: null,
      replyTo: null,
      sentAt: '2022-03-14T16:40:35Z',
      receivedAt: '2022-03-14T16:41:03Z',
      createdAt: '2022-03-14T16:41:03Z',
      messageId: '<7d41aa93@quarrystonepartners.com>',
      inReplyTo: null,
      messageReferences: null,
      providerMessageId: 'pm-5b1e7fc4',
      mailboxHash: 'case-XXSR0006',
      spamScore: 0.9,
      // Not yet opened.
      readStatus: readStatusItem(READ_STATUS.UNREAD),
    },
  ]
}

/**
 * The one address the mock treats as suppressed, and the record the API returns
 * with its 409. Postmark suppresses an address after a hard bounce or a spam
 * complaint and then refuses further mail to it at their end -- which is why
 * `sendToSuppressed` records an attempt rather than delivering one.
 */
export const SUPPRESSED_ADDRESS = 'ray.kowalski@brightmill.example'

export const SUPPRESSION_RECORD = {
  recipient: SUPPRESSED_ADDRESS,
  event: 'HardBounce',
  bounceType: 'HardBounce',
  canReactivate: true,
  occurredAt: '2022-02-11T08:03:00Z',
}

export function findServiceRequestEmailSuppression(
  address: string | null | undefined
) {
  // Service Request-insensitive: the server matches on a lowered address, and a
  // differently-cased one must still match (its own integration test says so).
  return address && address.toLowerCase() === SUPPRESSED_ADDRESS.toLowerCase()
    ? SUPPRESSION_RECORD
    : undefined
}

/**
 * The server's 409 for suppressed recipients (services/email_composition.py
 * suppressed_problem): ProblemDetails with its `extra` flattened into the top
 * level -- the first suppression's fields, plus `suppressed` listing every one.
 * Shared by a case reply and an activity email's send.
 */
export function suppressedRecipientsBody(
  suppressions: NonNullable<
    ReturnType<typeof findServiceRequestEmailSuppression>
  >[]
) {
  return {
    status: 409,
    title: 'Recipient address is suppressed',
    detail:
      suppressions
        .map(
          (s) =>
            `${s.recipient} was suppressed after a ${s.bounceType}, ` +
            'so Postmark will refuse this message.'
        )
        .join(' ') +
      ' It can be reactivated in Postmark, but consider finding another ' +
      'way to reach ' +
      (suppressions.length === 1 ? 'this contact.' : 'these contacts.') +
      ' Send anyway with sendToSuppressed.',
    ...suppressions[0],
    suppressed: suppressions,
  }
}

const STORAGE_KEY = 'servicerequestemails'

let serviceRequestEmailListData: IServiceRequestEmailSeed[] | undefined

/**
 * A bespoke loader rather than getListDataFunctionFactory, and the reason is
 * measured rather than stylistic.
 *
 * That factory is bounded on TListDataTypes, every member of which carries
 * IEntityDataItem's `[propName: string]: any` index signature. IServiceRequestEmail has no
 * index signature -- it is a closed, hand-listed shape, deliberately not an
 * entity -- so adding it to that union strips the index signature from the union
 * as a whole and breaks ~25 unrelated mock modules that read arbitrary
 * properties off it. Tried it: the ratchet went 3099 -> 3129. Twenty lines here
 * is much the cheaper side of that trade.
 */
export async function getServiceRequestEmailListData(): Promise<
  IServiceRequestEmailSeed[]
> {
  const fn = `${mn} getServiceRequestEmailListData |`
  if (serviceRequestEmailListData) {
    return serviceRequestEmailListData
  }
  const fromStorage = await getEntityListDataFromLocalForage<
    IServiceRequestEmailSeed[]
  >(STORAGE_KEY)
  if (fromStorage) {
    serviceRequestEmailListData = fromStorage.map(withReadStatus)
    return serviceRequestEmailListData
  }
  serviceRequestEmailListData = await getStartingServiceRequestEmailListData()
  await _storeEntityDataListToLocalForage({
    entityKeyBase: STORAGE_KEY,
    listData: serviceRequestEmailListData as never,
    callingFn: fn,
  })
  return serviceRequestEmailListData
}

/**
 * Mock data saved before cynque-api-server #1814 has no `readStatus`. The
 * server's backfill makes every existing inbound message Read (or Replied), so
 * a saved inbound row without one reads as Read; anything else, null. An ADDED
 * key, so renameLegacyPersistedMockKeys.ts has no part in it.
 */
function withReadStatus(
  serviceRequestEmail: IServiceRequestEmailSeed
): IServiceRequestEmailSeed {
  if ('readStatus' in serviceRequestEmail) {
    return serviceRequestEmail
  }
  return {
    ...serviceRequestEmail,
    readStatus:
      serviceRequestEmail.direction === 'inbound'
        ? readStatusItem(READ_STATUS.READ)
        : null,
  }
}

async function storeCaseEmails(
  list: IServiceRequestEmailSeed[],
  callingFn: string
) {
  await _storeEntityDataListToLocalForage({
    entityKeyBase: STORAGE_KEY,
    listData: list as never,
    callingFn,
  })
}

function toWireShape({
  _caseId,
  ...serviceRequestEmail
}: IServiceRequestEmailSeed): IServiceRequestEmail {
  return serviceRequestEmail
}

export async function getServiceRequestEmailDataById(
  id: TEntityId
): Promise<IServiceRequestEmail | undefined> {
  const list = await getServiceRequestEmailListData()
  const found = list?.find(
    (serviceRequestEmail) => serviceRequestEmail.id === id
  )
  return found ? toWireShape(found) : undefined
}

/**
 * Every email on a case, in the order the overview returns them.
 *
 * ⚠ Includes the ones whose `body` is null. The server's overview does, so the
 * mock must too -- otherwise the "an auto-reply must not appear on the card"
 * case cannot occur locally and the filtering that prevents it is untested.
 */
export async function getServiceRequestEmailListDataByServiceRequestId(
  serviceRequestId: TEntityId
): Promise<IServiceRequestEmail[]> {
  const fn = `${mn} getServiceRequestEmailListDataByServiceRequestId |`
  const list = await getServiceRequestEmailListData()
  if (!list) {
    throw new Error(`${fn} returned undefined/null`)
  }
  return list
    .filter(
      (serviceRequestEmail) => serviceRequestEmail._caseId === serviceRequestId
    )
    .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
    .map(toWireShape)
}

/**
 * Record an outbound reply's envelope, in `queued`.
 *
 * ⚠ It STAYS queued. Nothing in the mock sends, exactly as nothing in the real
 * POST /replies sends -- that endpoint writes a queued message and returns it,
 * and a worker does the rest. A mock that advanced this to `sent` would
 * be making a promise the server does not make, and would quietly remove the
 * only local way to see a reply sitting unsent.
 *
 * Lives here rather than in the cases handler so `_caseId` stays inside the one
 * module that knows it is mock bookkeeping and not part of the wire shape.
 */
export async function appendOutboundReplyEmail({
  serviceRequestId,
  body,
  sentBy,
  subject,
  toAddresses,
  ccAddresses,
  bccAddresses,
  inbound,
}: {
  serviceRequestId: TEntityId
  // As the user wrote it; the server stores exactly what was sent.
  body: string
  // The agent who wrote it.
  sentBy: IServiceRequestEmail['sentBy']
  subject: string
  toAddresses: string[]
  ccAddresses: string[]
  bccAddresses: string[]
  // What it replies to, for threading -- none on a case opened in the app.
  inbound: IServiceRequestEmail | undefined
}): Promise<IServiceRequestEmail> {
  const list = await getServiceRequestEmailListData()
  const serial = String(list.length + 1).padStart(4, '0')
  const now = new Date().toISOString()
  const seed: IServiceRequestEmailSeed = {
    _caseId: serviceRequestId,
    id: `XXCAE${serial}`,
    direction: 'outbound',
    body,
    sentBy,
    repeatOf: null,
    deliveryState: 'queued',
    // As the user sent it (prefilled "Re: <case descr>"). ⚠ Never from the
    // inbound message's subject: the most recent inbound on a case is often an
    // AUTO-REPLY, and a reply then went out as "Re: Automatic reply: ...".
    subject,
    fromAddress: 'servicerequests@wxtechnologygroup.com',
    fromName: 'WX Technology Group Support',
    toAddresses,
    ccAddresses: ccAddresses.length ? ccAddresses : null,
    bccAddresses: bccAddresses.length ? bccAddresses : null,
    replyTo: 'servicerequests@wxtechnologygroup.com',
    sentAt: null,
    receivedAt: null,
    createdAt: now,
    messageId: null,
    inReplyTo: inbound?.messageId ?? null,
    messageReferences: inbound?.messageId ? [inbound.messageId] : null,
    providerMessageId: null,
    mailboxHash: `case-${serviceRequestId}`,
    spamScore: null,
    // Outbound: no read status (cynque-api-server #1814).
    readStatus: null,
  }
  list.push(seed)
  await _storeEntityDataListToLocalForage({
    // storageKey IS the entity key base -- see getListDataFunctionFactory.
    entityKeyBase: STORAGE_KEY,
    listData: list as never,
    callingFn: `${mn} appendOutboundReplyEmail |`,
  })
  return toWireShape(seed)
}

/** The most recent inbound message, or undefined if the case has none. */
export async function getLatestInboundServiceRequestEmail(
  serviceRequestId: TEntityId
): Promise<IServiceRequestEmail | undefined> {
  const emails = await getServiceRequestEmailListDataByServiceRequestId(
    serviceRequestId
  )
  const inbound = emails.filter((email) => email.direction === 'inbound')
  return inbound[inbound.length - 1]
}

/**
 * Move every email on `fromServiceRequestId` to `toServiceRequestId` -- the mock half of a case
 * merge (POST /api/servicerequests/:id/merges). Here, like appendOutboundReplyEmail, so
 * `_caseId` stays inside the module that knows it is mock bookkeeping.
 */
export async function moveServiceRequestEmailsToServiceRequest({
  fromServiceRequestId,
  toServiceRequestId,
}: {
  fromServiceRequestId: TEntityId
  toServiceRequestId: TEntityId
}): Promise<number> {
  const list = await getServiceRequestEmailListData()
  const moving = list.filter(
    (serviceRequestEmail) =>
      serviceRequestEmail._caseId === fromServiceRequestId
  )
  for (const serviceRequestEmail of moving) {
    serviceRequestEmail._caseId = toServiceRequestId
  }
  if (moving.length) {
    await _storeEntityDataListToLocalForage({
      entityKeyBase: STORAGE_KEY,
      listData: list as never,
      callingFn: `${mn} moveServiceRequestEmailsToServiceRequest |`,
    })
  }
  return moving.length
}

/**
 * PATCH /api/emails/{id}/readStatus, for a case email (cynque-api-server
 * #1814). `undefined` when no case email has the id -- the caller then looks
 * among the activity emails -- otherwise the refusal or the updated email.
 */
export async function setServiceRequestEmailReadStatus(
  id: TEntityId,
  toId: unknown
): Promise<
  | { refusal: string }
  | { serviceRequestEmail: IServiceRequestEmail }
  | undefined
> {
  const list = await getServiceRequestEmailListData()
  const found = list.find(
    (serviceRequestEmail) => serviceRequestEmail.id === id
  )
  if (!found) {
    return undefined
  }
  const refusal = readStatusChangeRefusal(found, toId)
  if (refusal) {
    return { refusal }
  }
  found.readStatus = readStatusItem(toId as number)
  await storeCaseEmails(list, `${mn} setServiceRequestEmailReadStatus |`)
  return { serviceRequestEmail: toWireShape(found) }
}

/**
 * Why a reply's `inReplyToEmailId` is refused on this case, or null -- see
 * inReplyToRefusal.
 */
export async function serviceRequestReplyTargetRefusal(
  serviceRequestId: TEntityId,
  emailId: TEntityId
): Promise<string | null> {
  const list = await getServiceRequestEmailListData()
  const found = list.find(
    (serviceRequestEmail) => serviceRequestEmail.id === emailId
  )
  return inReplyToRefusal(
    found && {
      // inReplyToRefusal's own name for the owning record.
      caseId: found._caseId,
      direction: found.direction,
    },
    serviceRequestId
  )
}

/**
 * A reply went to this message: it becomes Replied, whatever it was. The
 * server does this in the reply's own transaction (cynque-api-server #1814).
 */
export async function markServiceRequestEmailReplied(
  id: TEntityId
): Promise<void> {
  const list = await getServiceRequestEmailListData()
  const found = list.find(
    (serviceRequestEmail) => serviceRequestEmail.id === id
  )
  if (!found || found.direction !== 'inbound') {
    return
  }
  found.readStatus = readStatusItem(READ_STATUS.REPLIED)
  await storeCaseEmails(list, `${mn} markServiceRequestEmailReplied |`)
}
